import { useMutation } from '@apollo/client';
import {
  Box,
  Checkbox,
  Radio,
  TextFieldSize,
  ToastAnimateFrom,
  ToastContent,
  ToastPosition,
  ToastType,
} from '@bechtel/gds';
import { CommodityName, UiTextEnum } from '@bechtel/sr-types';
import { useFormikContext } from 'formik';
import { get, isEqual, toUpper } from 'lodash';
import { CommodityFooter, FormField, MultiLingualText, TextFieldComponent, useLanguage } from '~/components';
import MessageModal from '~/components/organisms/message-modal/message';
import { useToast } from '~/hooks';
import { RenameTagsFormikState } from '../types';
import { ContentStyled, FormContainerStyled, FormStyled, ListSelectionStyled, SectionHeaderStyled } from './styled';
import {
  buildGqlMutation,
  commodities,
  customMetaData,
  getEntityNameForMutation,
  getEntityNameForMutationResponse,
  getPicklistName,
  getSanitizedValues,
} from './utils';

const RenameTagsForm = () => {
  const { showToast } = useToast();
  const { getMultiLingualTextByKey } = useLanguage();
  const { values, dirty, touched, handleReset, setFieldValue } = useFormikContext<RenameTagsFormikState>();
  const entityText = getMultiLingualTextByKey('RECORDS');
  const entityName = getEntityNameForMutation(values.commodityType as CommodityName);
  const entityNameForResponse = getEntityNameForMutationResponse(values.commodityType as CommodityName);
  const query = buildGqlMutation(entityName, entityNameForResponse);
  const isBlockCommodity = [CommodityName.EQUIPMENT_BLOCK, CommodityName.EQUIPMENT_CODE_BLOCK].includes(
    values.commodityType as CommodityName
  );
  const notRevCardCommodity = [CommodityName.PACKAGE, CommodityName.COLOR_CODE].includes(
    values.commodityType as CommodityName
  );

  const [renameTag, { data: renameTagData, loading }] = useMutation(query, {
    onCompleted: (data) => {
      if (data[`rename${entityName}`]?.[`${entityNameForResponse}`]) {
        showToast(
          UiTextEnum.SAVED_SUCCESSFULLY_TOAST,
          [getMultiLingualTextByKey(values.commodityType), entityText],
          ToastType.INFO
        );
      }
    },
    update(cache) {
      cache.evict({ fieldName: `search${entityName}s` });
      cache.gc();
    },
    onError: () => {
      showToast(
        UiTextEnum.ERROR_WHILE_UPDATING_TOAST,
        [getMultiLingualTextByKey(toUpper(values.commodityType)), entityText],
        ToastType.WARNING
      );
    },
  });

  const saveHandler = async () => {
    const sanitizedValues = getSanitizedValues(values);
    await renameTag({
      variables: {
        [`rename${entityName}Input`]: { ...sanitizedValues },
      },
    });
  };

  const errorKeys = get(renameTagData, `rename${entityName}.errors`, []);

  const createTagMetadata = (key: string, memberName: string, pickListName: string = '') => ({
    ...customMetaData,
    memberName,
    description: getMultiLingualTextByKey(key),
    alternateDescription: getMultiLingualTextByKey(key),
    columnPrompt: getMultiLingualTextByKey(key),
    pickListName: pickListName ? pickListName : null,
    pickListType: 0,
  });

  const oldTagMetadata = createTagMetadata(
    'CURRENT_TAG',
    'oldTag',
    getPicklistName(values.commodityType as CommodityName)
  );
  const newTagMetadata = createTagMetadata('REVISED_TAG', 'newTag');
  const blockParentTagMetadata = createTagMetadata(
    values.commodityType === CommodityName.EQUIPMENT_BLOCK ? 'Equipment' : 'Equipment Code',
    'parentTag',
    getPicklistName(
      values.commodityType === CommodityName.EQUIPMENT_BLOCK ? CommodityName.EQUIPMENT : CommodityName.EQUIPMENT_CODE
    )
  );

  const renderCommodityList = (startIndex: number, endIndex: number) =>
    commodities.slice(startIndex, endIndex).map((commodity, index) => (
      <ListSelectionStyled
        key={index}
        onClick={() => {
          if (values.commodityType === commodity) return;
          //[TODO]: SETR-1434
          if (
            [CommodityName.REEL, CommodityName.EQUIPMENT_BLOCK, CommodityName.EQUIPMENT_CODE_BLOCK].includes(
              commodity as CommodityName
            )
          )
            return;
          handleReset(); // Reset the form when changing commodity
          setFieldValue('commodityType', commodity);
        }}
        //[TODO]: SETR-1434
        disabled={[CommodityName.REEL, CommodityName.EQUIPMENT_BLOCK, CommodityName.EQUIPMENT_CODE_BLOCK].includes(
          commodity as CommodityName
        )}
      >
        <Box mr={{ sm: '500' }}>
          <Radio
            size={TextFieldSize.MEDIUM}
            checked={values.commodityType === commodity}
            readOnly
            //[TODO]: SETR-1434
            disabled={[CommodityName.REEL, CommodityName.EQUIPMENT_BLOCK, CommodityName.EQUIPMENT_CODE_BLOCK].includes(
              commodity as CommodityName
            )}
          />
        </Box>
        <MultiLingualText size="300" color="grey.900">
          {commodity}
        </MultiLingualText>
      </ListSelectionStyled>
    ));

  return (
    <Box flexGrow={{ sm: '1' }} flexDirection={{ sm: 'column' }}>
      <FormStyled>
        <FormContainerStyled>
          <ContentStyled>
            <Box flexDirection={{ sm: 'column' }}>
              <SectionHeaderStyled>
                <MultiLingualText size="400" weight="600" color="grey.800">
                  Component Type
                </MultiLingualText>
              </SectionHeaderStyled>
              <Box gap={{ sm: '900' }}>
                <Box flexDirection={{ sm: 'column' }}>{renderCommodityList(0, 6)}</Box>
                <Box flexDirection={{ sm: 'column' }}>{renderCommodityList(6, commodities.length)}</Box>
              </Box>
            </Box>
            {isBlockCommodity && (
              <Box mt={{ sm: '450' }}>
                <FormField
                  alwaysEditable={true}
                  memberName="tag"
                  inferedFieldName="parentTag"
                  required={true}
                  customMetadataColumn={blockParentTagMetadata}
                  tagValidationType={'TAG_DOES_NOT_EXIST'}
                >
                  <TextFieldComponent />
                </FormField>
              </Box>
            )}
            <Box mb={{ sm: '450' }} mt={{ sm: '450' }}>
              <FormField
                alwaysEditable={true}
                memberName="tag"
                inferedFieldName="oldTag"
                required={true}
                customMetadataColumn={oldTagMetadata}
                tagValidationType={'TAG_DOES_NOT_EXIST'}
              >
                <TextFieldComponent />
              </FormField>
            </Box>
            <Box mb={{ sm: '450' }}>
              <FormField
                alwaysEditable={true}
                memberName="newTag"
                required={true}
                customMetadataColumn={newTagMetadata}
              >
                <TextFieldComponent />
              </FormField>
            </Box>

            <ListSelectionStyled
              disabled={notRevCardCommodity}
              onClick={() => {
                if (notRevCardCommodity) return;
                setFieldValue('isRevCardAuthorized', !values.isRevCardAuthorized);
              }}
              pt={{ sm: '450' }}
            >
              <Box mr={{ sm: '400' }}>
                <Checkbox
                  checked={values.isRevCardAuthorized}
                  disabled={notRevCardCommodity}
                  size={TextFieldSize.MEDIUM}
                  readOnly
                />
              </Box>
              <Box>
                <MultiLingualText size="300" color="grey.800">
                  Authorize revised installation card
                </MultiLingualText>
              </Box>
            </ListSelectionStyled>
          </ContentStyled>
        </FormContainerStyled>
      </FormStyled>
      <CommodityFooter
        saveHandler={saveHandler}
        loading={loading}
        disableSave={!touched || loading || isEqual(values.oldTag, values.newTag)}
        disableCancel={!dirty || !touched || loading}
        cancelHandler={handleReset}
      />
      {errorKeys?.length > 0 && <MessageModal messageKeys={errorKeys} />}
      <ToastContent position={ToastPosition.MIDDLE} animateFrom={ToastAnimateFrom.BOTTOM} />
    </Box>
  );
};

export default RenameTagsForm;
